
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import useMeasure from "react-use-measure";
import { ParagraphButtonData, ParagraphButtonDataType } from "@/model/paragraph";
import { toSpliced, withArray } from "@/util/arrayUtil";
import { getNewButton, isValidParagraphUrl } from "@/util/helper/paragraphHelper";
import Tooltip from "@/component/Tooltip";
import Button from "@/component/Button";
import { TextInput } from "@/component/input";
import { SortableItemWrapper, SortableWrapper, toSortableItem, SortableItem } from "@/component/SortableWrapper";
import ChevronArrowIcon from "@/component/icon/ChevronArrowIcon";
import { AddIcon, DeleteIcon } from "@/assets/svgs/icons";

import { ButtonInputs, ButtonTypeDropdown } from ".";
import VariableInputGroup from "./VariableInputGroup";


const BREAKPOINT_WIDTH = 500;

interface ButtonSortableListProps {
    required?: boolean;
    useIntent?: boolean;
    className?: string;
    defaultButtons: ParagraphButtonData[];
    onChange: (buttons: ParagraphButtonData[]) => void;
}

const ButtonSortableList: React.FC<ButtonSortableListProps> = (props) => {
    const { defaultButtons, onChange, required = false, useIntent = false, className: propsClassName } = props;

    const intl = useIntl();
    const [listMeasureRef, { width: listWidth }] = useMeasure();

    const buttonTypes: ParagraphButtonDataType[] = ["text", "next", "link", "back", "scenario", ...(useIntent ? ["intent"] as const : [])];

    const [showVariable, setShowVariable] = useState<boolean>(true);
    const [sortableButtons, setSortableButtons] = useState<SortableItem<ParagraphButtonData>[]>([]);

    const isInvalidLinkButton = (button: ParagraphButtonData): boolean => {
        if (button.button_type !== "link") {
            return false;
        }
        if (!isValidParagraphUrl(button.link_mobile ?? "")) {
            return true;
        }
        if (button.link_pc && !isValidParagraphUrl(button.link_pc ?? "")) {
            return true;
        }
        return false;
    };

    const update = (buttons: SortableItem<ParagraphButtonData>[]) => {
        setSortableButtons(buttons);
        onChange(buttons.map(item => item.data));
    };

    useEffect(() => {
        const sortableButtons = defaultButtons.map(toSortableItem);
        if (sortableButtons.length === 0 && required) {
            sortableButtons.push(toSortableItem(getNewButton()));
        }
        setSortableButtons(sortableButtons);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="paragraph-btn-list"
             className={classNames("col-span-2 flex flex-col gap-1.5", propsClassName)}
        >
            <div className="h-6 flex items-center gap-1">
                <div className="field-label">
                    {intl.formatMessage({ id: "i04029" })}
                </div>
                <Button variant="text-transparent"
                        className="size-4"
                        aria-label={intl.formatMessage({ id: "b0009" })}
                        onClick={() => update([...sortableButtons, toSortableItem(getNewButton())])}
                >
                    <AddIcon className="size-3" />
                </Button>
                {sortableButtons.some(button => button.data?.button_type === "next") && (
                    <Tooltip placement="top"
                             offset={4}
                             message={intl.formatMessage({ id: "i04023" })}
                    >
                        <Button variant="text-transparent"
                                className="transition-transform duration-200"
                                aria-expanded={showVariable}
                                aria-label={intl.formatMessage({ id: "i04043" })}
                                onClick={() => setShowVariable(prev => !prev)}
                        >
                            <ChevronArrowIcon direction={showVariable ? "up" : "down"}
                                              className="size-4"
                            />
                        </Button>
                    </Tooltip>
                )}
            </div>
            {required && sortableButtons.length === 0 && (
                <div className="text-invalid">
                    {intl.formatMessage({ id: "i04081" })}
                </div>
            )}
            <div ref={listMeasureRef}>
                <SortableWrapper items={sortableButtons}
                                 className="flex flex-col gap-2"
                                 onDragSortEnd={update}
                >{sortableButtons.map((button, index) => (
                    <SortableItemWrapper key={button.id}
                                         item={button}
                                         aria-label={`${intl.formatMessage({ id: "i04029" })} ${index + 1}`}
                    >{dragHandle => (
                        <div className="flex-1 flex flex-col gap-2">
                            {isInvalidLinkButton(button.data) && (
                                <div className="text-invalid">
                                    {intl.formatMessage({ id: "i04077" })}
                                </div>
                            )}
                            <div className="grid grid-cols-[auto_auto_auto_1fr_auto] gap-x-1 gap-y-1">
                                <div className="label-input-grid-label mr-0.5 scale-[1.2]">
                                    {dragHandle}
                                </div>
                                <TextInput required
                                           className={classNames(
                                               "h-input-default",
                                               listWidth < BREAKPOINT_WIDTH ? "w-24" : "w-[200px]"
                                           )}
                                           value={button.data?.label ?? ""}
                                           placeholder={intl.formatMessage({ id: "i04075" })}
                                           onChange={e => update(withArray(sortableButtons, index, { ...button, data: { ...button.data, label: e.target.value } }))}
                                />
                                <ButtonTypeDropdown width={listWidth < BREAKPOINT_WIDTH ? 120 : 160}
                                                    options={buttonTypes}
                                                    button={button.data}
                                                    onChange={data => update(withArray(sortableButtons, index, { ...button, data }))}
                                />
                                <ButtonInputs button={button.data}
                                              onChange={data => update(withArray(sortableButtons, index, { ...button, data }))}
                                />
                                <Button variant="text-transparent"
                                        disabled={required && sortableButtons.length <= 1}
                                        className="label-input-grid-label -col-end-1 ml-1 mr-2 flex-none disabled:opacity-0 disabled:cursor-default"
                                        aria-label={intl.formatMessage({ id: "b0006" })}
                                        onClick={() => update(toSpliced(sortableButtons, index, 1))}
                                >
                                    <DeleteIcon className="size-4 text-point-red" />
                                </Button>
                                {showVariable && button.data?.button_type === "next" && (
                                    <VariableInputGroup required={false}
                                                        className="mb-1.5 col-start-2 col-span-4"
                                                        data={button.data?.options}
                                                        onChange={options => update(withArray(sortableButtons, index, { ...button, data: { ...button.data, options } }))}
                                    />
                                )}
                            </div>
                        </div>
                    )}</SortableItemWrapper>
                ))}</SortableWrapper>
            </div>
        </div>
    );
};

export default ButtonSortableList;
